import { formatMoneyCents } from './format';

/**
 * Whole dollars, optionally with one or two decimal places: "12", "12.5", or
 * "12.50". No leading sign (a price is never negative) and no thousands
 * separators, matching how a price field is actually typed at a counter.
 */
const MONEY_INPUT_PATTERN = /^\d+(\.\d{1,2})?$/;

/**
 * Converts a typed dollar amount ("12.50") into integer cents the API
 * expects. Returns `undefined` for anything that is not a valid, non-negative
 * amount, including a plain NaN parse, so the caller can show one Spanish
 * field error instead of sending a broken number to the server.
 */
export function dollarsToCents(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!MONEY_INPUT_PATTERN.test(trimmed)) return undefined;
  const dollars = Number.parseFloat(trimmed);
  if (Number.isNaN(dollars) || dollars < 0) return undefined;
  return Math.round(dollars * 100);
}

/** The inverse, for pre-filling an edit form from a stored `priceCents`. */
export function centsToDollarsInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * The API validates `priceCents` as an integer in 0..10_000_000
 * (`BusinessServiceCreateSchema` in services/api). The client has to hold the
 * same ceiling: a price above it comes back as a generic 400 banner with the
 * offending field buried, which reads to the owner as "the app broke" rather
 * than "that price is too high". Kept as a named constant so the two bounds
 * are compared, not remembered.
 */
export const MAX_PRICE_CENTS = 10_000_000;

export const PRICE_FORMAT_MESSAGE = 'Escribe un precio válido, por ejemplo 12.50.';
export const PRICE_TOO_HIGH_MESSAGE = `El precio máximo es ${formatMoneyCents(MAX_PRICE_CENTS)}.`;

/**
 * A parsed price, or the one Spanish message to show under the price field.
 * The two failures are told apart on purpose: "that is not a number" and "that
 * number is too big" are different mistakes and deserve different answers.
 */
export type PriceInputResult = { ok: true; cents: number } | { ok: false; message: string };

/** What the price field submits: bounded, in cents, or a message to show. */
export function parsePriceInput(raw: string): PriceInputResult {
  const cents = dollarsToCents(raw);
  if (cents === undefined) return { ok: false, message: PRICE_FORMAT_MESSAGE };
  if (cents > MAX_PRICE_CENTS) return { ok: false, message: PRICE_TOO_HIGH_MESSAGE };
  return { ok: true, cents };
}
